import { socialApiClient } from '@/shared/api-client/social-api-client';
import {
  type AddCreatorRequestDTO,
  addCreatorsSchema,
  type CreateSavedListOutreachQueueRequestDTO,
  type CreateSavedListOutreachQueueResponseDTO,
  createSavedListOutreachQueueRequestSchema,
  type CreatorsInListDTO,
  type CreatorsListRequestDTO,
  creatorsListRequestSchema,
  type ExcludeList,
  type ExcludeListDTO,
  type RemoveCreatorRequestDTO,
  removeCreatorsSchema,
  type SavedListCampaignDraftCreatorRequestDTO,
  type SavedListCampaignDraftCreatorsResponseDTO,
  savedListCampaignDraftCreatorRequestSchema,
  savedListCampaignDraftCreatorsResponseSchema,
  type SavedList,
  type SavedListOutreachStatusItemDTO,
  savedListOutreachStatusItemSchema,
  savedListOutreachStatusesResponseSchema,
  type UpdateSavedListOutreachStatusRequestDTO,
  updateSavedListOutreachStatusRequestSchema,
} from '.';
import type { SOURCE_PLATFORM_TYPE } from '../public-filter';

export const FetchSavedListLists = async (
  workspace_id: string,
  signal?: AbortSignal,
): Promise<SavedList[]> => {
  const response = await socialApiClient.get('creators/saved-lists', {
    searchParams: {
      workspace_id,
    },
    signal,
  });
  const data = await response.json<{
    lists: SavedList[];
  }>();
  return data.lists;
};

export const CreateSavedList = async (
  params: {
    workspace_id: string;
    title: string;
  },
  signal?: AbortSignal,
): Promise<SavedList> => {
  const response = await socialApiClient.post('creators/saved-lists', {
    json: params,
    signal,
  });
  return await response.json();
};

export const DeleteSavedList = async (listId: string, signal?: AbortSignal): Promise<void> => {
  await socialApiClient.delete(`creators/saved-lists/${listId}`, { signal });
};

export const AddCreatorsToSavedList = async (
  listId: string,
  request: AddCreatorRequestDTO,
  signal?: AbortSignal,
): Promise<void> => {
  const parsedRequest = addCreatorsSchema.parse(request);
  await socialApiClient.post(`creators/saved-lists/${listId}/creators`, {
    json: parsedRequest,
    signal,
  });
};

export const RemoveCreatorsFromSavedList = async (
  listId: string,
  request: RemoveCreatorRequestDTO,
  signal?: AbortSignal,
): Promise<void> => {
  const parsedRequest = removeCreatorsSchema.parse(request);
  await socialApiClient.patch(`creators/saved-lists/${listId}/creators`, {
    json: parsedRequest,
    signal,
  });
};

export const FetchSavedCreatorsList = async (
  listId: string,
  signal?: AbortSignal,
): Promise<SavedList> => {
  const response = await socialApiClient.get(`creators/saved-lists/${listId}`, {
    signal,
  });
  return await response.json();
};

export const UpdateSavedListTitle = async (
  listId: string,
  title: string,
  signal?: AbortSignal,
): Promise<SavedList> => {
  const response = await socialApiClient.patch(`creators/saved-lists/${listId}/title`, {
    json: { title },
    signal,
  });
  return await response.json();
};

export const FetchCreatorsInSavedList = async (
  listId: string,
  params: CreatorsListRequestDTO | null,
  signal?: AbortSignal,
): Promise<{
  creators: Array<CreatorsInListDTO>;
  page: number;
  total_pages: number;
  total_count: number;
}> => {
  const parsedRequest = creatorsListRequestSchema.parse(params);
  const response = await socialApiClient.get(`creators/saved-lists/${listId}/creators`, {
    searchParams: parsedRequest || undefined,
    signal,
  });
  return await response.json();
};

export const FetchExcludeList = async (
  params: {
    workspace_id: string;
    cursor: string | null;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<ExcludeList> => {
  const response = await socialApiClient.get('creators/exclude-list', {
    searchParams: {
      workspace_id: params.workspace_id,
      cursor: params.cursor || undefined,
      limit: params.limit,
    },
    signal,
  });
  return await response.json<ExcludeListDTO>();
};

export const ExcludeCreators = async (
  params: {
    workspace_id: string;
    creator_id: string;
  },
  signal?: AbortSignal,
): Promise<{ creator_id: string }> => {
  await socialApiClient.post('creators/exclude-list', {
    json: params,
    signal,
  });
  return {
    creator_id: params.creator_id,
  };
};

export const UnExcludeCreators = async (
  params: {
    workspace_id: string;
    creator_id: string;
  },
  signal?: AbortSignal,
): Promise<{ creator_id: string }> => {
  await socialApiClient.delete('creators/exclude-list', {
    searchParams: params,
    signal,
  });
  return { creator_id: params.creator_id };
};

type FetchHandleListFromSavedListResponse = {
  creators: {
    username: string;
    platform?: SOURCE_PLATFORM_TYPE;
  }[];
};

export const FetchHandleListFromSavedList = async (
  listId: string,
): Promise<FetchHandleListFromSavedListResponse> => {
  const res = await socialApiClient.get(`creators/saved-lists/${listId}/creator-handles`);
  return await res.json();
};

export const CreateSavedListOutreachQueue = async (
  listId: string,
  request: CreateSavedListOutreachQueueRequestDTO,
  signal?: AbortSignal,
): Promise<CreateSavedListOutreachQueueResponseDTO> => {
  const parsedRequest = createSavedListOutreachQueueRequestSchema.parse(request);
  const response = await socialApiClient.post(`creators/saved-lists/${listId}/outreach-queues`, {
    json: parsedRequest,
    signal,
  });
  return await response.json();
};

export const FetchSavedListOutreachStatuses = async (
  listId: string,
  signal?: AbortSignal,
): Promise<SavedListOutreachStatusItemDTO[]> => {
  const response = await socialApiClient.get(`creators/saved-lists/${listId}/outreach-statuses`, {
    signal,
  });
  const parsedResponse = savedListOutreachStatusesResponseSchema.parse(await response.json());
  return parsedResponse.statuses;
};

export const UpdateSavedListOutreachStatus = async (
  listId: string,
  request: UpdateSavedListOutreachStatusRequestDTO,
  signal?: AbortSignal,
): Promise<SavedListOutreachStatusItemDTO> => {
  const parsedRequest = updateSavedListOutreachStatusRequestSchema.parse(request);
  const response = await socialApiClient.patch(`creators/saved-lists/${listId}/outreach-statuses`, {
    json: parsedRequest,
    signal,
  });
  return savedListOutreachStatusItemSchema.parse(await response.json());
};

export const FetchSavedListCampaignDraftCreators = async (
  listId: string,
  request: SavedListCampaignDraftCreatorRequestDTO,
  signal?: AbortSignal,
): Promise<SavedListCampaignDraftCreatorsResponseDTO> => {
  const parsedRequest = savedListCampaignDraftCreatorRequestSchema.parse(request);
  const response = await socialApiClient.post(
    `creators/saved-lists/${listId}/campaign-draft-creators`,
    {
      json: parsedRequest,
      signal,
    },
  );
  return savedListCampaignDraftCreatorsResponseSchema.parse(await response.json());
};
